import React from 'react';

/**
 * SOL Transport – Page-load / full-screen spinner
 *
 * Icon style: flat bold delivery truck matching Flaticon #10778805
 *   – single-unit box truck, clean geometric shapes, vibrant flat colours
 *   – truck bounces & drives on a minimal road strip
 *   – indigo sweep progress bar at the bottom
 *
 * Props:
 *   message  – label shown below (default: 'Loading…')
 *   size     – 'sm' | 'md' | 'lg'
 *   overlay  – true → fixed fullscreen dark backdrop
 */

/* ───────────────────────── Flat tyre ───────────────────────── */
function Tyre({ cx, cy, r = 13 }) {
  return (
    <g>
      <circle cx={cx} cy={cy} r={r}          fill="#1e293b" />
      <circle cx={cx} cy={cy} r={r * 0.55}   fill="#334155" />
      <circle cx={cx} cy={cy} r={r * 0.22}   fill="#94a3b8" />
      <g>
        <animateTransform attributeName="transform" type="rotate"
          from={`0 ${cx} ${cy}`} to={`360 ${cx} ${cy}`}
          dur="0.85s" repeatCount="indefinite" />
        {[0, 60, 120, 180, 240, 300].map(deg => {
          const a = (deg * Math.PI) / 180;
          return (
            <line key={deg}
              x1={cx + Math.cos(a) * r * 0.24} y1={cy + Math.sin(a) * r * 0.24}
              x2={cx + Math.cos(a) * r * 0.50} y2={cy + Math.sin(a) * r * 0.50}
              stroke="#64748b" strokeWidth="2" strokeLinecap="round" />
          );
        })}
      </g>
    </g>
  );
}

/* ──────────────── Flaticon-style single-unit box truck ──────────────
   Local coords:  truck W≈222  cab on right, cargo box on left
   Wheel baseline: y = 80
────────────────────────────────────────────────────────────────────── */
function FlatTruck({ uid }) {
  return (
    <g>
      {/* ── CARGO BOX ── */}
      <rect x="0" y="10" width="154" height="62" rx="5" fill="#dbeafe" />
      <rect x="0" y="10" width="154" height="8"  rx="4" fill="#3b82f6" />
      <rect x="0" y="65" width="154" height="7"  rx="2" fill="#2563eb" opacity="0.4" />
      {[44, 88, 132].map(x => (
        <line key={x} x1={x} y1="18" x2={x} y2="65"
          stroke="#bfdbfe" strokeWidth="1.5" strokeDasharray="3 3" />
      ))}
      <rect x="4"  y="38" width="6"  height="14" rx="3"   fill="#93c5fd" opacity="0.7" />
      <rect x="0"  y="22" width="5"  height="14" rx="2.5" fill="#ef4444">
        <animate attributeName="opacity" values="1;0.35;1" dur="1.4s" repeatCount="indefinite" />
      </rect>
      <rect x="0"  y="38" width="5"  height="9"  rx="2"   fill="#f97316" opacity="0.9" />
      <text x="77" y="48" textAnchor="middle"
        fontFamily="'Inter','Helvetica Neue',Arial,sans-serif"
        fontSize="11" fontWeight="900" fill="#1d4ed8" letterSpacing="2.5">SOL</text>
      <text x="77" y="60" textAnchor="middle"
        fontFamily="'Inter','Helvetica Neue',Arial,sans-serif"
        fontSize="7" fontWeight="700" fill="#3b82f6" letterSpacing="2">TRANSPORT</text>

      {/* ── HITCH ── */}
      <rect x="150" y="58" width="16" height="8" rx="2" fill="#475569" />
      <circle cx="158" cy="66" r="3" fill="#64748b" />

      {/* ── CAB ── */}
      <rect x="152" y="20" width="68" height="52" rx="8" fill={`url(#${uid}cab)`} />
      <path d="M163 20 Q168 4 188 2 L210 2 Q218 3 216 14 L216 20Z"
        fill="#bfdbfe" stroke="#2563eb" strokeWidth="1.5" />
      <path d="M165 20 Q170 6 188 3 L214 3 L214 20Z" fill="white" opacity="0.20" />
      <line x1="172" y1="8" x2="182" y2="18"
        stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.18" />
      <line x1="184" y1="21" x2="184" y2="70" stroke="#1d4ed8" strokeWidth="1.2" opacity="0.4" />
      <rect x="172" y="46" width="10" height="3" rx="1.5" fill="#93c5fd" opacity="0.8" />
      <rect x="152" y="68" width="68" height="3" rx="1.5" fill="#93c5fd" opacity="0.22" />
      <rect x="216" y="14" width="3"  height="7" rx="1.5" fill="#1e40af" />
      <rect x="214" y="10" width="9"  height="6" rx="2"   fill="#1e3a8a" />
      <rect x="214" y="60" width="8"  height="10" rx="3"  fill="#1e3a8a" />
      <rect x="214" y="24" width="10" height="8"  rx="3"  fill="#fef9c3">
        <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />
      </rect>
      <rect x="215" y="34" width="7"  height="4" rx="2" fill="#fde68a" opacity="0.75" />
      {[24, 30, 36, 42].map(y => (
        <line key={y} x1="213" y1={y} x2="222" y2={y}
          stroke="#93c5fd" strokeWidth="1" opacity="0.45" />
      ))}

      {/* exhaust puffs */}
      {[0, 1].map(i => (
        <circle key={i} cx="178" cy="0" r={5 - i} fill="#cbd5e1" opacity="0.3">
          <animate attributeName="cy"      from="0" to={-16 - i * 9}
            dur={`${0.8 + i * 0.28}s`} begin={`${i * 0.35}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" from="0.35" to="0"
            dur={`${0.8 + i * 0.28}s`} begin={`${i * 0.35}s`} repeatCount="indefinite" />
          <animate attributeName="r"       from={`${5 - i}`} to={`${9 - i}`}
            dur={`${0.8 + i * 0.28}s`} begin={`${i * 0.35}s`} repeatCount="indefinite" />
        </circle>
      ))}

      {/* ── WHEELS ── */}
      <Tyre cx={30}  cy={80} r={14} />
      <Tyre cx={118} cy={80} r={14} />
      <Tyre cx={178} cy={80} r={14} />
    </g>
  );
}

/* ───────────────────────── Main component ───────────────────── */
export default function TruckLoader({ message = 'Loading\u2026', size = 'md', overlay = false }) {
  const rawId = React.useId();
  const uid   = rawId.replace(/:/g, '');

  const scale = size === 'sm' ? 0.70 : size === 'lg' ? 1.32 : 1.0;
  const VIEWW = 420;
  const VIEWH = 120;
  const W = Math.round(VIEWW * scale);
  const H = Math.round(VIEWH * scale);

  const wrapStyle = overlay ? {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'rgba(15,23,42,0.85)',
    backdropFilter: 'blur(6px)',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', gap: 16,
  } : {
    display: 'inline-flex', flexDirection: 'column',
    alignItems: 'center', gap: 10,
  };

  const dashXs = [0, 56, 112, 168, 224, 280, 336, 392, 448];

  return (
    <div style={wrapStyle} role="status" aria-label={message || 'Loading'}>
      <div style={{
        background: '#ffffff',
        borderRadius: 18,
        boxShadow: '0 8px 36px rgba(0,0,0,0.10), 0 1px 0 rgba(0,0,0,0.04)',
        border: '1px solid #e8edf5',
        overflow: 'hidden',
        display: 'block',
      }}>
        <svg viewBox={`0 0 ${VIEWW} ${VIEWH}`} width={W} height={H}
          xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
          <defs>
            <linearGradient id={`${uid}sky`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#eff6ff" />
              <stop offset="100%" stopColor="#f8fbff" />
            </linearGradient>
            <linearGradient id={`${uid}road`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#d1d9e6" />
              <stop offset="100%" stopColor="#bcc6d4" />
            </linearGradient>
            <linearGradient id={`${uid}cab`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#3b82f6" />
              <stop offset="100%" stopColor="#1d4ed8" />
            </linearGradient>
            <linearGradient id={`${uid}prog`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"   stopColor="#6366f1" stopOpacity="0" />
              <stop offset="30%"  stopColor="#6366f1" />
              <stop offset="70%"  stopColor="#818cf8" />
              <stop offset="100%" stopColor="#818cf8" stopOpacity="0" />
            </linearGradient>
            <clipPath id={`${uid}roadclip`}>
              <rect x="0" y="88" width={VIEWW} height="32" />
            </clipPath>
          </defs>

          {/* Sky */}
          <rect width={VIEWW} height={VIEWH} fill={`url(#${uid}sky)`} />

          {/* Cloud 1 */}
          <g opacity="0.6">
            <animateTransform attributeName="transform" type="translate"
              from="-140,0" to={`${VIEWW + 80},0`}
              dur="12s" repeatCount="indefinite" />
            <ellipse cx="0"   cy="22" rx="26" ry="14" fill="white" />
            <ellipse cx="-17" cy="26" rx="16" ry="11" fill="white" />
            <ellipse cx="17"  cy="26" rx="15" ry="11" fill="white" />
            <ellipse cx="0"   cy="28" rx="20" ry="10" fill="white" />
          </g>
          {/* Cloud 2 */}
          <g opacity="0.4">
            <animateTransform attributeName="transform" type="translate"
              from={`${-VIEWW * 0.4},0`} to={`${VIEWW + 120},0`}
              dur="18s" begin="-7s" repeatCount="indefinite" />
            <ellipse cx="0"   cy="15" rx="18" ry="9"  fill="white" />
            <ellipse cx="-12" cy="19" rx="12" ry="7"  fill="white" />
            <ellipse cx="12"  cy="19" rx="11" ry="7"  fill="white" />
          </g>

          {/* Road */}
          <rect x="0" y="88" width={VIEWW} height="32" fill={`url(#${uid}road)`} />
          <rect x="0" y="88" width={VIEWW} height="3"  fill="#aab4c4" />
          <rect x="0" y="116" width={VIEWW} height="4" fill="#9ca8b8" />

          {/* Scrolling dashes */}
          <g clipPath={`url(#${uid}roadclip)`}>
            <g>
              <animateTransform attributeName="transform" type="translate"
                from="0,0" to="-56,0" dur="0.6s" repeatCount="indefinite" />
              {dashXs.map(x => (
                <rect key={x} x={x} y="100" width="32" height="5" rx="2.5"
                  fill="#8594a8" opacity="0.5" />
              ))}
            </g>
          </g>

          {/* Truck drive loop */}
          <g>
            <animateTransform attributeName="transform" type="translate"
              from="-260,0" to={`${VIEWW + 20},0`}
              dur="3.8s" repeatCount="indefinite" />
            <g>
              <animateTransform attributeName="transform" type="translate"
                values="0,0; 0,-3; 0,1; 0,-1; 0,0"
                keyTimes="0; 0.25; 0.55; 0.78; 1"
                dur="0.65s" repeatCount="indefinite" additive="sum" />
              <g transform="translate(0,8)">
                <FlatTruck uid={uid} />
              </g>
            </g>
          </g>

          {/* Progress sweep bar */}
          <rect x="0" y="113" width={VIEWW} height="7" fill="#e2e8f0" />
          <rect x="0" y="113" width="160" height="7" fill={`url(#${uid}prog)`}>
            <animateTransform attributeName="transform" type="translate"
              from={`-${VIEWW},0`} to={`${VIEWW},0`}
              dur="1.9s" repeatCount="indefinite" />
          </rect>
        </svg>
      </div>

      {message && (
        <div style={{
          color: overlay ? '#cbd5e1' : '#6b7280',
          fontSize: Math.round(11 * scale),
          fontWeight: 700,
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
        }}>
          {message}
        </div>
      )}
    </div>
  );
}

